import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ExternalLink, Package, ShoppingBag, Users, IndianRupee, Receipt, Wallet, AlertTriangle, FilePlus2,
  CheckCircle2, Ban, FileText, CalendarClock,
} from "lucide-react";
import api, { apiErr } from "@/lib/api";
import DataTable from "@/components/app/DataTable";
import Modal from "@/components/app/Modal";
import InvoiceBadge, { SubscriptionBadge } from "@/components/app/InvoiceBadge";
import { StatusBadge } from "../vendor/Overview";
import { Field, useValidatedForm } from "@/components/app/FormKit";
import { btn, btnOutline, btnPrimary, cardCls, fmtDate, fmtDateTime, inr, pageSub, pageTitle } from "@/components/app/ui";

const METHODS = [
  { value: "bank_transfer", label: "Bank transfer" }, { value: "upi", label: "UPI" }, { value: "cash", label: "Cash" },
  { value: "cheque", label: "Cheque" }, { value: "online", label: "Online / gateway" }, { value: "other", label: "Other" },
];
const methodLabel = (m) => METHODS.find((x) => x.value === m)?.label || (m ? m.replace(/_/g, " ") : "—");

export default function AdminSubscriptionDetail() {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const [payFor, setPayFor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/admin/subscriptions/${storeId}`).then(({ data }) => { setD(data); setError(""); })
    .catch((err) => setError(apiErr(err.response?.data?.detail) || "Could not load this subscription.")), [storeId]);
  useEffect(() => { load(); }, [load]);

  const validate = useCallback((f) => {
    const e = {};
    if (!f.method) e.method = "Choose how the payment was received.";
    if (f.reference.length > 60) e.reference = "Reference must be 60 characters or fewer.";
    return e;
  }, []);
  const form = useValidatedForm({ method: "bank_transfer", reference: "" }, validate);

  const openPay = (inv) => { form.reset({ method: "bank_transfer", reference: "" }); setPayFor(inv); };

  const markPaid = async () => {
    if (!form.check()) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/invoices/${payFor.id}/mark-paid`, { method: form.values.method, reference: form.values.reference.trim() });
      toast.success(data.restored
        ? `Invoice ${payFor.number} marked paid. ${d.store.name} has been restored to the ${d.subscription.plan_name} plan.`
        : `Invoice ${payFor.number} marked as paid.`);
      setPayFor(null); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const voidInvoice = useCallback(async (inv) => {
    if (!window.confirm(`Void invoice ${inv.number} (${inr(inv.amount)})? The vendor will no longer owe it.`)) return;
    try {
      const { data } = await api.post(`/admin/invoices/${inv.id}/void`);
      toast.success(data.restored ? `Invoice ${inv.number} voided and the store was restored to its subscribed plan.` : `Invoice ${inv.number} voided.`);
      load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/subscriptions/${storeId}/invoices`);
      toast.success(`Invoice ${data.number} generated for ${inr(data.amount)}. It is due ${fmtDate(data.due_date)}.`);
      load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const columns = useMemo(() => [
    { key: "number", header: "Invoice", cell: (i) => <span className="font-mono font-semibold text-orange-600 whitespace-nowrap">{i.number}</span> },
    { key: "plan_name", header: "Plan", hideBelow: "sm" },
    { key: "period_start", header: "Billing period", hideBelow: "md", accessor: (i) => i.period_start, cell: (i) => <span className="text-slate-600 whitespace-nowrap">{fmtDate(i.period_start)} – {fmtDate(i.period_end)}</span> },
    { key: "amount", header: "Amount", align: "right", accessor: (i) => i.amount, cell: (i) => <span className="font-semibold">{inr(i.amount)}</span> },
    { key: "display_status", header: "Status", accessor: (i) => i.display_status, cell: (i) => (
      <div><InvoiceBadge status={i.display_status} />{i.days_overdue > 0 && <div className="text-[11px] text-red-600 mt-0.5">{i.days_overdue}d overdue</div>}</div>
    ) },
    { key: "created_at", header: "Created", accessor: (i) => i.created_at, cell: (i) => <span className="text-slate-600 whitespace-nowrap">{fmtDateTime(i.created_at)}</span> },
    { key: "updated_at", header: "Updated", hideBelow: "lg", accessor: (i) => i.updated_at, cell: (i) => <span className="text-slate-600 whitespace-nowrap">{fmtDateTime(i.updated_at)}</span> },
    { key: "due_date", header: "Due", hideBelow: "md", accessor: (i) => i.due_date, cell: (i) => (
      <span className={`whitespace-nowrap ${i.display_status === "overdue" ? "text-red-600 font-semibold" : "text-slate-600"}`}>{fmtDate(i.due_date)}</span>
    ) },
    { key: "paid_at", header: "Paid", hideBelow: "md", accessor: (i) => i.paid_at || "", cell: (i) => (
      i.paid_at ? <div><div className="text-slate-600 whitespace-nowrap">{fmtDateTime(i.paid_at)}</div><div className="text-[11px] text-slate-500">{methodLabel(i.payment_method)}</div></div> : <span className="text-slate-400">—</span>
    ) },
    { key: "actions", header: "", sortable: false, align: "right", cell: (i) => (
      <div className="flex justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
        {i.status === "pending" && (
          <>
            <button onClick={() => openPay(i)} data-testid={`invoice-markpaid-${i.id}`} className={`${btn} !h-8 !px-2.5 !text-[12px] border border-green-200 bg-green-50 text-green-700 hover:bg-green-100`}><CheckCircle2 size={13} /> Mark paid</button>
            <button onClick={() => voidInvoice(i)} data-testid={`invoice-void-${i.id}`} className={`${btn} !h-8 !px-2.5 !text-[12px] border border-slate-200 bg-white text-slate-600 hover:border-red-300 hover:text-red-600`}><Ban size={13} /> Void</button>
          </>
        )}
      </div>
    ) },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [voidInvoice]);

  if (error) {
    return (
      <div className="space-y-4">
        <Link to="/admin/subscriptions" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-blue-600 hover:underline"><ArrowLeft size={14} /> Back to subscriptions</Link>
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-[13px] text-red-700">{error}</div>
      </div>
    );
  }
  if (!d) return <div className="text-slate-500 text-[13px]">Loading…</div>;

  const { store, owner, subscription: sub, stats, invoices } = d;
  const lapsed = sub.status === "lapsed";
  const lapsedInvoice = invoices.find((i) => i.id === sub.lapsed_invoice_id);
  const statCards = [
    { label: "Products", value: stats.products, icon: Package, tone: "blue" },
    { label: "Orders", value: stats.orders, icon: ShoppingBag, tone: "orange" },
    { label: "Customers", value: stats.customers, icon: Users, tone: "blue" },
    { label: "Store revenue (GMV)", value: inr(stats.gmv), icon: IndianRupee, tone: "green" },
    { label: "Invoiced to date", value: inr(stats.invoiced), icon: Receipt, tone: "blue" },
    { label: "Paid to date", value: inr(stats.paid), icon: Wallet, tone: "green" },
    { label: "Outstanding", value: inr(stats.outstanding.amount), icon: AlertTriangle, tone: stats.outstanding.overdue_count ? "red" : "orange", sub: stats.outstanding.count ? `${stats.outstanding.count} unpaid invoice${stats.outstanding.count > 1 ? "s" : ""}` : "All paid" },
  ];
  const TONES = {
    blue: "from-blue-50 to-white border-blue-100 bg-blue-100 text-blue-600", orange: "from-orange-50 to-white border-orange-100 bg-orange-100 text-orange-600",
    green: "from-green-50 to-white border-green-100 bg-green-100 text-green-600", red: "from-red-50 to-white border-red-100 bg-red-100 text-red-600",
  };
  const tone = (t) => { const [a, b, c, e, f] = TONES[t].split(" "); return { card: `${a} ${b} ${c}`, chip: `${e} ${f}` }; };

  return (
    <div className="space-y-4" data-testid="admin-subscription-detail">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/admin/subscriptions")} aria-label="Back to subscriptions" data-testid="subscription-back"
            className="w-9 h-9 rounded-lg border border-slate-200 bg-white flex items-center justify-center text-slate-600 hover:text-blue-600 hover:border-blue-300"><ArrowLeft size={16} /></button>
          <span className="w-11 h-11 rounded-full bg-orange-100 text-orange-600 text-base font-bold flex items-center justify-center">{store.name?.[0]?.toUpperCase()}</span>
          <div>
            <h1 className={`${pageTitle} flex items-center gap-2 flex-wrap`}>{store.name} <SubscriptionBadge status={sub.status} /> <StatusBadge status={store.status} label={`Store ${store.status}`} /></h1>
            <p className={pageSub}><a href={`/store/${store.slug}`} target="_blank" rel="noreferrer" className="font-mono text-blue-600 hover:underline inline-flex items-center gap-1">/{store.slug} <ExternalLink size={11} /></a> · {owner.name} · {owner.email}</p>
          </div>
        </div>
        <button onClick={generate} disabled={busy || sub.price <= 0 || lapsed} data-testid="generate-invoice" className={btnPrimary}
          title={sub.price <= 0 ? "Free plan — nothing to invoice" : lapsed ? "Collect the overdue invoice first" : "Generate the invoice for the current period if it is due"}>
          <FilePlus2 size={14} /> Generate invoice
        </button>
      </div>

      {lapsed && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex items-start gap-3 text-[13px] text-red-800" data-testid="subscription-lapsed-alert">
          <AlertTriangle size={17} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">Moved to the Free plan on {fmtDate(sub.lapsed_at)} — {lapsedInvoice ? `invoice ${lapsedInvoice.number} (${inr(lapsedInvoice.amount)}) was not paid within ${d.grace_days} days.` : "an invoice was not paid in time."}</div>
            <div>The store returns to the <b>{sub.plan_name}</b> plan automatically as soon as the overdue invoice is paid or voided.</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {statCards.map((c) => {
          const t = tone(c.tone);
          return (
            <div key={c.label} className={`rounded-xl border bg-gradient-to-br ${t.card} px-4 py-3 ${c.label === "Outstanding" ? "" : ""}`}>
              <div className="flex items-start justify-between"><div className="text-[12px] text-slate-500">{c.label}</div><span className={`w-8 h-8 rounded-lg flex items-center justify-center ${t.chip}`}><c.icon size={15} /></span></div>
              <div className="text-[22px] font-bold text-neutral-900 leading-tight">{c.value}</div>
              {c.sub && <div className="text-[11px] text-slate-500">{c.sub}</div>}
            </div>
          );
        })}
        <div className={`${cardCls} px-4 py-3`}>
          <div className="flex items-start justify-between"><div className="text-[12px] text-slate-500">Next invoice</div><span className="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-100 text-blue-600"><CalendarClock size={15} /></span></div>
          <div className="text-[18px] font-bold text-neutral-900 leading-tight">{sub.next_invoice_date ? fmtDate(sub.next_invoice_date) : "—"}</div>
          <div className="text-[11px] text-slate-500">{lapsed ? "Paused while lapsed" : sub.price <= 0 ? "Free plan" : "Generated automatically"}</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-3">
        <section className={`${cardCls} p-4 lg:col-span-2`}>
          <h2 className="text-[14px] font-bold text-neutral-900 mb-3">Subscription</h2>
          <dl className="grid sm:grid-cols-3 gap-x-6 gap-y-3 text-[13px]">
            <div><dt className="text-slate-500 text-[12px]">Subscribed plan</dt><dd className="font-semibold text-neutral-900">{sub.plan_name} <span className="text-slate-500 font-normal">· {inr(sub.price)}/mo</span></dd></div>
            <div><dt className="text-slate-500 text-[12px]">Current plan (what the store can use)</dt><dd className={`font-semibold ${lapsed ? "text-red-600" : "text-neutral-900"}`}>{sub.effective_plan_name}{lapsed && " — suspended"}</dd></div>
            <div><dt className="text-slate-500 text-[12px]">Status</dt><dd><SubscriptionBadge status={sub.status} /></dd></div>
            <div><dt className="text-slate-500 text-[12px]">Subscribed since</dt><dd className="text-neutral-900">{fmtDate(sub.started_at)}</dd></div>
            <div><dt className="text-slate-500 text-[12px]">Last updated</dt><dd className="text-neutral-900">{fmtDateTime(sub.updated_at || sub.started_at)}</dd></div>
            <div><dt className="text-slate-500 text-[12px]">{lapsed ? "Lapsed on" : sub.restored_at ? "Last restored" : "Payment terms"}</dt>
              <dd className="text-neutral-900">{lapsed ? fmtDate(sub.lapsed_at) : sub.restored_at ? fmtDate(sub.restored_at) : `Due ${d.grace_days} days after invoice`}</dd></div>
          </dl>
          <p className="mt-3 text-[12px] text-slate-500 border-t border-slate-100 pt-3">
            Invoices are created monthly. If an invoice isn&apos;t paid within {d.grace_days} days, the store moves to the Free plan until it is paid, then returns to <b>{sub.plan_name}</b>.
          </p>
        </section>
        <section className={`${cardCls} p-4`}>
          <h2 className="text-[14px] font-bold text-neutral-900 mb-3">Store</h2>
          <dl className="space-y-2 text-[13px]">
            <div className="flex justify-between gap-3"><dt className="text-slate-500">Category</dt><dd className="text-neutral-900 text-right">{store.business_category || "—"}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-slate-500">Owner</dt><dd className="text-neutral-900 text-right">{owner.name || "—"}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-slate-500">Mobile</dt><dd className="text-neutral-900 text-right">{owner.mobile || "—"}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-slate-500">Created</dt><dd className="text-neutral-900 text-right">{fmtDate(store.created_at)}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-slate-500">Custom domain</dt><dd className="text-neutral-900 text-right">{store.custom_domain || "—"}</dd></div>
          </dl>
        </section>
      </div>

      <section className="space-y-2">
        <div>
          <h2 className="text-[14px] font-bold text-neutral-900">Invoices</h2>
          <p className="text-[12px] text-slate-500">{invoices.length} invoice{invoices.length === 1 ? "" : "s"} with created, updated, due and paid dates.</p>
        </div>
        <DataTable testId="subscription-invoices-table" exportName={`invoices-${store.slug}`} columns={columns} data={invoices} rowTestId={(i) => `invoice-row-${i.id}`}
          searchKeys={["number", "plan_name"]} searchPlaceholder="Search invoice number or plan…"
          filters={[{ key: "status", label: "All statuses", accessor: (i) => i.display_status, options: [{ value: "paid", label: "Paid" }, { value: "pending", label: "Pending" }, { value: "overdue", label: "Overdue" }, { value: "void", label: "Void" }] }]}
          dateField={{ label: "Created", accessor: (i) => i.created_at }} defaultSort={{ key: "created_at", dir: "desc" }}
          emptyIcon={FileText} emptyTitle="No invoices yet" emptyText="Invoices are generated for paid plans." />
      </section>

      <Modal open={!!payFor} onClose={() => setPayFor(null)} size="md" testId="markpaid-modal" title={payFor ? `Mark ${payFor.number} as paid` : ""}
        subtitle={payFor ? `${inr(payFor.amount)} · ${payFor.plan_name} plan · due ${fmtDate(payFor.due_date)}` : ""}
        footer={<>
          <button onClick={() => setPayFor(null)} className={btnOutline}>Cancel</button>
          <button onClick={markPaid} disabled={saving} data-testid="markpaid-confirm" className={btnPrimary}>{saving ? "Saving…" : "Confirm payment"}</button>
        </>}>
        <div className="grid grid-cols-2 gap-x-3 gap-y-3">
          <Field className="col-span-2 sm:col-span-1" label="Payment method" required type="select" options={METHODS} t="markpaid-method" {...form.bind("method")} />
          <Field className="col-span-2 sm:col-span-1" label="Reference / UTR" maxLength={60} t="markpaid-reference" placeholder="Optional" {...form.bind("reference")} />
          {lapsed && <p className="col-span-2 text-[12px] text-slate-500">If this was the overdue invoice, the store will be restored to the <b>{sub.plan_name}</b> plan immediately.</p>}
        </div>
      </Modal>
    </div>
  );
}
